type BridgeMethod = 'getAccessToken' | 'requestInAppPurchase';

type BridgeRequest = {
    source: 'semrush-rankings';
    requestId: string;
    method: BridgeMethod;
    args: unknown[];
};

type BridgeResponse = {
    source: 'semrush-host-dev';
    requestId: string;
    ok: boolean;
    result?: unknown;
    error?: string;
};

export function createParentSemrushBridge(w: Window, targetOrigin: string) {
    let nextId = 1;

    return {
        init: async () => undefined,
        client: <TArgs extends unknown[], TResult>(method: BridgeMethod, ...args: TArgs): Promise<TResult> => {
            const requestId = String(nextId++);

            return new Promise<TResult>((resolve, reject) => {
                const onMessage = (event: MessageEvent<BridgeResponse>) => {
                    if (event.origin !== targetOrigin) {
                        return;
                    }

                    if (event.data?.source !== 'semrush-host-dev' || event.data.requestId !== requestId) {
                        return;
                    }

                    w.removeEventListener('message', onMessage);

                    if (event.data.ok) {
                        resolve(event.data.result as TResult);
                        return;
                    }

                    reject(new Error(event.data.error || 'Bridge request failed'));
                };

                const payload: BridgeRequest = {
                    source: 'semrush-rankings',
                    requestId,
                    method,
                    args,
                };

                w.addEventListener('message', onMessage);
                w.parent.postMessage(payload, targetOrigin);
            });
        },
    };
}

export function createStandaloneSemrushBridge(jwt: string) {
    return {
        init: async () => ({
            allowedGetParams: ['jwt'],
            layout: 1,
        }),
        client: async <TArgs extends unknown[], TResult>(method: BridgeMethod, ..._args: TArgs): Promise<TResult> => {
            if (method === 'getAccessToken') {
                return jwt as TResult;
            }

            if (method === 'requestInAppPurchase') {
                return undefined as TResult;
            }

            throw new Error(`Unsupported standalone method: ${method}`);
        },
    };
}
